import React, { useState, useEffect } from 'react';
import {
  Clock,
  Calendar,
  GraduationCap,
  BookOpen,
  Building,
  Image,
  Shuffle,
  Award,
  ToggleLeft,
  ToggleRight
} from 'lucide-react';
import { clsx } from 'clsx';

const Toggle = ({ enabled, onChange, label }) => (
  <button
    type="button"
    onClick={() => onChange(!enabled)}
    className="flex items-center space-x-2"
  >
    {enabled ? (
      <ToggleRight className="w-8 h-8 text-sigma-600" />
    ) : (
      <ToggleLeft className="w-8 h-8 text-gray-400" />
    )}
    <span className="text-sm text-gray-700">{label}</span>
  </button>
);

const AssessmentSettings = ({ assessment, onUpdate }) => {
  const [settings, setSettings] = useState({
    timeLimitEnabled: false,
    timeLimit: 60,
    startDateEnabled: false,
    startDate: '',
    dueDateEnabled: false,
    dueDate: '',
    gradeLevel: '',
    subject: '',
    institutionType: '',
    emblemEnabled: false,
    emblemUrl: '',
    shuffleQuestions: false,
    shuffleAnswers: false,
    passingScore: 50
  });

  useEffect(() => {
    if (assessment) {
      setSettings(prev => ({
        ...prev,
        timeLimitEnabled: !!assessment.timeLimit,
        timeLimit: assessment.timeLimit || 60,
        startDateEnabled: !!assessment.startDate,
        startDate: assessment.startDate || '',
        dueDateEnabled: !!assessment.dueDate,
        dueDate: assessment.dueDate || '',
        gradeLevel: assessment.gradeLevel || '',
        subject: assessment.subject || '',
        institutionType: assessment.institutionType || '',
        emblemEnabled: !!assessment.emblemUrl,
        emblemUrl: assessment.emblemUrl || '',
        shuffleQuestions: assessment.shuffleQuestions || false,
        shuffleAnswers: assessment.shuffleAnswers || false,
        passingScore: assessment.passingScore ?? 50
      }));
    }
  }, [assessment]);

  const handleChange = (field, value) => {
    const updated = { ...settings, [field]: value };
    setSettings(updated);

    const payload = {};
    payload.timeLimit = updated.timeLimitEnabled ? updated.timeLimit : null;
    payload.startDate = updated.startDateEnabled ? updated.startDate : null;
    payload.dueDate = updated.dueDateEnabled ? updated.dueDate : null;
    payload.gradeLevel = updated.gradeLevel;
    payload.subject = updated.subject;
    payload.institutionType = updated.institutionType;
    payload.emblemUrl = updated.emblemEnabled ? updated.emblemUrl : null;
    payload.shuffleQuestions = updated.shuffleQuestions;
    payload.shuffleAnswers = updated.shuffleAnswers;
    payload.passingScore = updated.passingScore;

    onUpdate(payload);
  };

  const gradeLevels = [
    '', 'Grade 1', 'Grade 2', 'Grade 3', 'Grade 4', 'Grade 5', 'Grade 6',
    'Grade 7', 'Grade 8', 'Grade 9', 'Grade 10', 'Grade 11', 'Grade 12',
    'University', 'Professional'
  ];

  const subjects = [
    '', 'Mathematics', 'Physical Sciences', 'Life Sciences', 'English',
    'Afrikaans', 'History', 'Geography', 'Business Studies', 'Accounting',
    'Economics', 'Computer Science', 'Engineering', 'Other'
  ];

  const institutionTypes = [
    '', 'Primary School', 'High School', 'TVET College', 'University', 'Other'
  ];

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-8">
      {/* Time Limit */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center mb-4">
          <Clock className="w-5 h-5 text-sigma-600 mr-2" />
          <h3 className="text-lg font-medium text-gray-900">Time Limit</h3>
        </div>
        <Toggle
          enabled={settings.timeLimitEnabled}
          onChange={(val) => handleChange('timeLimitEnabled', val)}
          label="Enable time limit"
        />
        {settings.timeLimitEnabled && (
          <div className="mt-4 flex items-center space-x-2">
            <input
              type="number"
              min="1"
              max="600"
              value={settings.timeLimit}
              onChange={(e) => handleChange('timeLimit', parseInt(e.target.value) || 60)}
              className="w-24 px-3 py-2 border border-gray-300 rounded-md focus:ring-sigma-500 focus:border-sigma-500"
            />
            <span className="text-sm text-gray-600">minutes</span>
          </div>
        )}
      </div>

      {/* Schedule */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center mb-4">
          <Calendar className="w-5 h-5 text-sigma-600 mr-2" />
          <h3 className="text-lg font-medium text-gray-900">Schedule</h3>
        </div>

        <div className="space-y-4">
          <div>
            <Toggle
              enabled={settings.startDateEnabled}
              onChange={(val) => handleChange('startDateEnabled', val)}
              label="Set start date"
            />
            {settings.startDateEnabled && (
              <div className="mt-2">
                <input
                  type="datetime-local"
                  value={settings.startDate}
                  onChange={(e) => handleChange('startDate', e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:ring-sigma-500 focus:border-sigma-500"
                />
              </div>
            )}
          </div>

          <div>
            <Toggle
              enabled={settings.dueDateEnabled}
              onChange={(val) => handleChange('dueDateEnabled', val)}
              label="Set due date"
            />
            {settings.dueDateEnabled && (
              <div className="mt-2">
                <input
                  type="datetime-local"
                  value={settings.dueDate}
                  onChange={(e) => handleChange('dueDate', e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:ring-sigma-500 focus:border-sigma-500"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Classification */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center mb-4">
          <GraduationCap className="w-5 h-5 text-sigma-600 mr-2" />
          <h3 className="text-lg font-medium text-gray-900">Classification</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Grade Level</label>
            <select
              value={settings.gradeLevel}
              onChange={(e) => handleChange('gradeLevel', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-sigma-500 focus:border-sigma-500"
            >
              {gradeLevels.map((level) => (
                <option key={level} value={level}>
                  {level || 'Select grade level'}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
            <select
              value={settings.subject}
              onChange={(e) => handleChange('subject', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-sigma-500 focus:border-sigma-500"
            >
              {subjects.map((subj) => (
                <option key={subj} value={subj}>
                  {subj || 'Select subject'}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Institution Type</label>
            <select
              value={settings.institutionType}
              onChange={(e) => handleChange('institutionType', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-sigma-500 focus:border-sigma-500"
            >
              {institutionTypes.map((type) => (
                <option key={type} value={type}>
                  {type || 'Select institution type'}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Emblem */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center mb-4">
          <Image className="w-5 h-5 text-sigma-600 mr-2" />
          <h3 className="text-lg font-medium text-gray-900">Emblem</h3>
        </div>
        <Toggle
          enabled={settings.emblemEnabled}
          onChange={(val) => handleChange('emblemEnabled', val)}
          label="Show emblem on assessment"
        />
        {settings.emblemEnabled && (
          <div className="mt-4">
            <input
              type="url"
              value={settings.emblemUrl}
              onChange={(e) => handleChange('emblemUrl', e.target.value)}
              placeholder="Enter emblem image URL"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-sigma-500 focus:border-sigma-500"
            />
            {settings.emblemUrl && (
              <div className="mt-2">
                <img
                  src={settings.emblemUrl}
                  alt="Emblem preview"
                  className="h-16 w-16 object-contain rounded border border-gray-200"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Shuffling */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center mb-4">
          <Shuffle className="w-5 h-5 text-sigma-600 mr-2" />
          <h3 className="text-lg font-medium text-gray-900">Shuffling</h3>
        </div>
        <div className="space-y-3">
          <Toggle
            enabled={settings.shuffleQuestions}
            onChange={(val) => handleChange('shuffleQuestions', val)}
            label="Shuffle question order"
          />
          <Toggle
            enabled={settings.shuffleAnswers}
            onChange={(val) => handleChange('shuffleAnswers', val)}
            label="Shuffle answer options"
          />
        </div>
      </div>

      {/* Passing Score */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center mb-4">
          <Award className="w-5 h-5 text-sigma-600 mr-2" />
          <h3 className="text-lg font-medium text-gray-900">Passing Score</h3>
        </div>
        <div className="flex items-center space-x-2">
          <input
            type="number"
            min="0"
            max="100"
            value={settings.passingScore}
            onChange={(e) => handleChange('passingScore', parseInt(e.target.value) || 0)}
            className="w-24 px-3 py-2 border border-gray-300 rounded-md focus:ring-sigma-500 focus:border-sigma-500"
          />
          <span className="text-sm text-gray-600">% required to pass</span>
        </div>
      </div>
    </div>
  );
};

export default AssessmentSettings;
